import React, { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentUpload from "@/components/DocumentUpload";
import { Info, Plus, Trash2, Wallet } from "lucide-react";
import { formatCLP } from "@/lib/format";
import MoneyInput from "@/components/MoneyInput";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export default function StepIncome({ app, patch, reload }) {
  const income = app.income || { adults: [], others: [] };

  const setAdult = (i, k, v) => { const a = [...income.adults]; a[i] = { ...a[i], [k]: v }; patch({ income: { ...income, adults: a } }); };
  const addAdult = () => patch({ income: { ...income, adults: [...income.adults, { id: uid(), nombre: "", rut: "", role: "otro", income_declared: null, no_documents: false, doc_type: "liquidacion" }] } });
  const rmAdult = (i) => patch({ income: { ...income, adults: income.adults.filter((_, x) => x !== i) } });

  const addOther = () => patch({ income: { ...income, others: [...(income.others || []), { id: uid(), tipo: "Honorarios", descripcion: "", amount: null }] } });
  const setOther = (i, k, v) => { const o = [...income.others]; o[i] = { ...o[i], [k]: v }; patch({ income: { ...income, others: o } }); };
  const rmOther = (i) => patch({ income: { ...income, others: income.others.filter((_, x) => x !== i) } });
  const missingIds = (income.others || []).some((o) => !o.id);
  useEffect(() => {
    if (missingIds) patch({ income: { ...income, others: income.others.map((o) => (o.id ? o : { ...o, id: uid() })) } });
  }, [missingIds]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Ingresos</h1>
        <p className="text-muted-foreground">Indica los ingresos <strong>brutos mensuales</strong> del grupo familiar.</p>
      </div>

      <div className="bg-secondary/60 border border-border rounded-xl p-5 flex gap-3">
        <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground">
          El <strong>ingreso bruto</strong> es el total antes de descuentos (imposiciones, salud, impuestos).
          No indiques el ingreso líquido. Encuentras este valor en tu liquidación de sueldo como "total haberes" o "remuneración bruta".
        </p>
      </div>

      {income.adults.map((a, i) => (
        <section key={a.id || i} className="bg-white rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold"><Wallet className="w-5 h-5 text-accent" /> {a.nombre || `Adulto ${i + 1}`}</h2>
            {a.role === "otro" && <button onClick={() => rmAdult(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></button>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {a.role === "otro" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre</Label>
                <Input className="h-12 rounded-xl mt-1.5" value={a.nombre || ""} onChange={(e) => setAdult(i, "nombre", e.target.value)} />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ingreso bruto mensual (CLP)</Label>
              <MoneyInput className="h-12 rounded-xl mt-1.5" value={a.income_declared} placeholder="Ej: 1.500.000"
                onChange={(v) => setAdult(i, "income_declared", v)} data-testid={`income-declared-${i}`} />
              {a.income_declared > 0 && a.income_declared < 100000 ? (
                <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid={`income-low-warning-${i}`}>
                  Escribiste {formatCLP(a.income_declared)} al mes. Revisa que sea el sueldo bruto mensual completo en pesos (ej: 1.500.000).
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Sueldo bruto mensual completo en pesos.</p>
              )}
            </div>
          </div>

          {a.no_documents ? (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`no-docs-legacy-${i}`}>
              Ahora es obligatorio adjuntar el respaldo de este ingreso para enviar la solicitud.{" "}
              <button type="button" className="underline font-semibold" onClick={() => setAdult(i, "no_documents", false)} data-testid={`no-docs-attach-${i}`}>Adjuntar documento</button>
            </div>
          ) : (
            <div>
              <div className="mb-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo de respaldo</Label>
                <Select value={a.doc_type || "liquidacion"} onValueChange={(v) => setAdult(i, "doc_type", v)}>
                  <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid={`doc-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liquidacion">Opción A: Últimas 3 liquidaciones de sueldo</SelectItem>
                    <SelectItem value="impuestos">Opción B: Declaración anual de impuestos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DocumentUpload appId={app.id} scope="income" adultId={a.id} docType={a.doc_type || "liquidacion"} documents={app.documents} onChange={reload} />
              {a.income_detected != null && (
                <div className={`mt-3 rounded-xl p-3 text-sm ${a.verification_status === "mismatch" ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-emerald-50 border border-emerald-200 text-emerald-900"}`} data-testid={`verification-${i}`}>
                  {a.verification_status === "verified" && <>✓ Ingreso verificado: {formatCLP(a.income_detected)}/mes</>}
                  {a.verification_status === "mismatch" && <>{a.adjustment_reason}</>}
                  {a.verification_status === "detected" && <>Ingreso detectado en documentos: {formatCLP(a.income_detected)}/mes</>}
                </div>
              )}
            </div>
          )}
        </section>
      ))}

      <Button variant="outline" onClick={addAdult} className="rounded-xl border-2" data-testid="btn-add-adult">
        <Plus className="w-4 h-4 mr-1" /> Agregar otro integrante que aporte ingresos
      </Button>

      <section className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Otros ingresos</h2>
          <Button variant="outline" size="sm" onClick={addOther} className="rounded-lg" data-testid="btn-add-other-income"><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Honorarios, arriendos, dividendos, pensiones, rentas de inversiones, otros.</p>
        <div className="space-y-4">
          {(income.others || []).map((o, i) => (
            <div key={o.id || i} className="border border-border rounded-xl p-4 space-y-3">
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Tipo</Label>
                <Input className="h-11 rounded-lg mt-1" value={o.tipo || ""} onChange={(e) => setOther(i, "tipo", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Descripción</Label>
                <Input className="h-11 rounded-lg mt-1" value={o.descripcion || ""} onChange={(e) => setOther(i, "descripcion", e.target.value)} />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Monto mensual</Label>
                  <MoneyInput className="h-11 rounded-lg mt-1" value={o.amount} onChange={(v) => setOther(i, "amount", v)} data-testid={`other-income-amount-${i}`} />
                </div>
                <button onClick={() => rmOther(i)} className="text-destructive mb-2" data-testid={`btn-remove-other-income-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div data-testid={`other-income-docs-${i}`}>
              <Label className="text-xs font-semibold uppercase text-slate-500">Respaldo obligatorio (boletas, contrato, cartola, liquidación de pensión)</Label>
              <div className="mt-2">
                <DocumentUpload appId={app.id} scope="other_income" adultId={o.id} docType="otro_ingreso" documents={app.documents} onChange={reload} />
              </div>
            </div>
            </div>
          ))}
          {(income.others || []).length === 0 && <p className="text-sm text-muted-foreground">Opcional.</p>}
        </div>
      </section>
    </div>
  );
}
